/** Settings · AI Brain › Channels and AI Receptionist (spec 15 §9.4, §9.5). */

import type { ReactNode } from 'react';

export type Channel = 'call' | 'sms' | 'email';

export const CHANNEL_WORD: Record<Channel, string> = { call: 'call', sms: 'sms', email: 'email' };

/**
 * One capability matrix, shown per channel. Every off switch carries its own
 * reason, and an action switched off is not refused — ELVA redirects the patient
 * to a channel where it is on (§9.4).
 */
export interface Capability {
  id: string;
  group: 'scheduling' | 'patient_info' | 'answering';
  label: string;
  desc: string;
  /** Why it is off, where the absence needs explaining. Rendered on the row. */
  offReason?: string;
  on: Record<Channel, boolean>;
}

export const CAPABILITY_GROUPS: { id: Capability['group']; label: string }[] = [
  { id: 'scheduling', label: 'Scheduling and managing appointments' },
  { id: 'patient_info', label: 'Adding and updating patient information' },
  { id: 'answering', label: 'Answering questions — from the Knowledge base' },
];

/** How ELVA checks who it is talking to. A name, plus at least one of these. */
export interface Verifier {
  id: string;
  /** The first one is worded per channel — number called from / texted from / address written from. */
  label: Record<Channel, string>;
  on: Record<Channel, boolean>;
}

/** What happens when nobody answers a transfer — the case a transfer rule usually forgets. */
export type NoAnswer = 'take_message' | 'voicemail' | 'next_number';

export interface TransferRule {
  id: string;
  condition: string;
  /** Call only — the one channel where a handover happens in real time. */
  destination: string;
  sayBefore?: string;
  ifNobodyPicksUp: NoAnswer;
}

/** SMS and email: nothing is transferred, and nothing is promised to the patient. */
export type ThreadBehaviour = 'keep_replying' | 'stop_replying';

export interface EscalationRule {
  id: string;
  condition: string;
  threadBehaviour: ThreadBehaviour;
  urgent: boolean;
}

/** A row in card 04, which may be read-only (from the Account app) or locked on. */
export interface IdentityRow {
  id: string;
  label: string;
  desc: string;
  value: string;
  /** Rendered mono, e.g. a phone number. */
  mono?: boolean;
  /** "From Account · loc_003" — the source, shown under the value. */
  source?: string;
  locked?: boolean;
  toggle?: boolean;
}

/** Card 06 on SMS — the mechanics of texting, not what ELVA says. */
export interface SmsRule {
  id: string;
  label: string;
  desc: string;
  locked?: boolean;
  enabled?: boolean;
  /** For the segment limit, which is a value rather than a switch. */
  value?: string;
  valueSuffix?: string;
}

export interface ChannelData {
  channel: Channel;
  titleLead: string;
  titleAccent: string;
  sub: ReactNode;
  /** Card 02's verifiers, and card 03's rules. */
  transferRules?: TransferRule[];
  escalationRules?: EscalationRule[];
  card03Title: string;
  card03Sub: ReactNode;
  identity: IdentityRow[];
  /** Call and SMS have a window; email deliberately has none. */
  quietHours?: { from: string; to: string; exceptionLabel: string; exceptionDesc: string };
  smsRules?: SmsRule[];
  emailTemplate?: EmailTemplate;
}

export interface EmailTemplate {
  practiceName: string;
  subject: string;
  greeting: string;
  body: string;
  buttonLabel: string;
  footerLines: string[];
  unsubscribeLabel: string;
  logoInitials: string;
  headerColour: string;
}

/* ─────────────────────── AI Receptionist (§9.5) ─────────────────────────── */

/** An extracted rule and what ELVA did with it. No state tags on the card (§9.5). */
export type RuleState = 'in_force' | 'conflict' | 'not_applied';

export interface ExtractedRule {
  id: string;
  quote: string;
  /** What ELVA will actually do about it. */
  effect: string;
  state: RuleState;
  /** The tinted sub-note: where it is enforced, or what would make it work. */
  note?: string;
  /** Present on a conflict: the one sentence that resolves it. */
  resolution?: string;
  enabled: boolean;
}

export interface ReceptionistData {
  greetingOpen: string;
  greetingClosed: string;
  emergencyText: string;
  emergencyRules: ExtractedRule[];
  emergencyLastRead: string;
  houseRulesText: string;
  houseRules: ExtractedRule[];
  houseRulesLastRead: string;
  /** Card 03 — four defaults, each stating what it costs. */
  doubleBookEmergency: boolean;
  heldSlots: string;
  heldUntil: string;
  skipInsuranceVerification: boolean;
  /** After-hours emergency routing (spec 44 §1). */
  afterHoursEmergency: 'page' | 'er' | 'morning' | 'message';
  /** Required when `afterHoursEmergency === 'page'`. */
  onCallNumber: string;
  /** Optional name of who answers the on-call number. */
  onCallWho: string;
}
